import { CALIBRATION_TYPE } from '../../../config/calibration';
import { API, DEFAULT_DYNAMIC_FIELDS_SCHEMA_ENDPOINT } from '../../../config/links';
import { DataSource, FormField, FormSchema, FormStep, SubmissionConfig, SubmissionMapping, SubmissionRuntimeMapping } from '../types';

export const newField = (n: number): FormField => ({
  key: `field_${n}`,
  label: `Field ${n}`,
  type: 'text',
  order: 0,
  options: [],
  validations: [],
  reset_rules: [],
  accept: [],
  multiple: false,
  show_in_review: true,
});
export const newStep = (n: number): FormStep => ({
  id: `step_${n}`,
  title: `Step ${n}`,
  fields: [],
});
export const blank = (): FormSchema => ({
  schema_version: '1.0',
  description: '',
  submit_label: 'Submit',
  steps: [newStep(1)],
  metadata: {},
});

export const builderFieldCount = (step?: FormStep) => step?.fields.length || 0;

export const defaultSubmission = (): SubmissionConfig => ({
  enabled: false,
  send_raw_values: false,
  request: { method: 'POST', endpoint: '', query: {}, path: {}, headers: {}, body: {} },
  body_encoding: 'json',
  content_type: 'application/json',
  mappings: [],
  runtime_mappings: [],
  error_mappings: [],
  success: { message: 'Form submitted successfully.', reset_form: false },
  timeout_seconds: 20,
});

export const newSubmissionMapping = (fields: FormField[], index: number): SubmissionMapping => ({
  target: fields[index]?.key || `field_${index + 1}`,
  source: { source: fields[index] ? 'field' : 'static', field: fields[index]?.key, value: '' },
  transform: 'identity',
  required: false,
  include_if_empty: false,
});

export const newRuntimeSubmissionMapping = (): SubmissionRuntimeMapping => ({
  target: 'input_data',
  scope: 'all',
  transform: 'identity',
  include_if_empty: false,
});

export const defaultDynamicSchemaSource = (allKeys: { key: string }[]): DataSource => {
  const keyFor = (...candidates: string[]) => candidates.find((candidate) => allKeys.some((item) => item.key === candidate));
  const query: DataSource['request']['query'] = {
    form_key: { source: 'static', value: 'calibration' },
    form_id: { source: 'static', value: 'start_calibration' },
    calibration_type: { source: 'static', value: CALIBRATION_TYPE.INITIAL },
  };
  const mappings: Array<[string, string[]]> = [
    ['sensor_type', ['sensorType', 'sensor_type']],
    ['category', ['category']],
    ['model_family', ['displacementSensorType', 'modelFamily', 'model_family']],
    ['model', ['model']],
    ['range', ['range']],
  ];
  const dependencies: string[] = [];
  mappings.forEach(([name, candidates]) => {
    const field = keyFor(...candidates);
    if (field) {
      query[name] = { source: 'field', field };
      dependencies.push(field);
    } else if (name === 'sensor_type') {
      // No sensor-type field on the form: use the form-level sensor type.
      query[name] = { source: 'context', key: 'sensor_type' };
    } else if (name === 'category') {
      // No category field on the form: use the form-level category.
      query[name] = { source: 'context', key: 'category' };
    }
  });
  return {
    request: { method: 'GET', endpoint: DEFAULT_DYNAMIC_FIELDS_SCHEMA_ENDPOINT, query, path: {}, headers: {}, body: {} },
    trigger: { type: 'step_enter_and_dependency_change', depends_on: dependencies, dependency_mode: 'any', trigger_fields: [], debounce_ms: 300, execute_when_inactive: false },
    response: { type: 'fields', path: '$.fields', value: 'value', label: 'label', field_key: 'key', field_label: 'label', field_type: 'type', field_placeholder: 'placeholder', field_help_text: 'help_text', field_validations: 'validation', default_field_type: 'text', dynamic_field_configs: {} },
  };
};

const LIBRARY_ENDPOINT_SUFFIX = '/calibration-data/schema';

/**
 * A step whose runtime schema comes from the dynamic field library: the usual calibration schema
 * source plus a static `entries` list naming the library entries the form selected.
 * The API resolves those entries from the published library when the step opens.
 */
export const buildLibraryDynamicSchema = (entryKeys: string[], allKeys: { key: string }[]): DataSource => {
  const base = defaultDynamicSchemaSource(allKeys);
  return { ...base, request: { ...base.request, query: { ...base.request.query, entries: { source: 'static', value: entryKeys.join(',') } } } };
};

/** The library entries a step selected, or undefined when its dynamic schema is not library-backed. */
export const getLibraryEntries = (source: DataSource | undefined): string[] | undefined => {
  if (!source || !source.request.endpoint.split('?')[0].endsWith(LIBRARY_ENDPOINT_SUFFIX)) return undefined;
  const param = source.request.query.entries;
  if (!param || param.source !== 'static' || typeof param.value !== 'string') return undefined;
  const keys = param.value.split(',').map((key) => key.trim()).filter(Boolean);
  return keys.length ? keys : undefined;
};

export const materializeLegacyDynamicGroups = (schema: FormSchema): FormSchema => {
  const steps = schema.steps.map((step) => {
    const existingKeys = new Set<string>();
    const fields: FormField[] = [];

    step.fields.forEach((field) => {
      if (field.type !== 'dynamic_group') {
        fields.push({ ...field });
        existingKeys.add(field.key);
        return;
      }
      const configs = field.data_source?.response.dynamic_field_configs || {};
      Object.entries(configs).forEach(([key, config]) => {
        if (existingKeys.has(key)) return;
        fields.push({
          key,
          label: config.label || key,
          type: (config.type || 'text') as FormField['type'],
          order: fields.length,
          placeholder: config.placeholder,
          help_text: config.help_text,
          default_value: config.default_value,
          options: (config.options || []).map((option) => ({ ...option })),
          validations: (config.validations || []).map((rule) => ({ ...rule })),
          visible_when: config.visible_when,
          required_when: config.required_when,
          disabled_when: config.disabled_when,
          reset_rules: (config.reset_rules || []).map((rule) => ({ ...rule, fields: [...rule.fields] })),
          accept: [...(config.accept || [])],
          multiple: !!config.multiple,
          min: config.min,
          max: config.max,
          step: config.step,
          show_in_review: config.show_in_review ?? field.show_in_review ?? true,
        });
        existingKeys.add(key);
      });
    });
    return { ...step, fields: fields.map((field, index) => ({ ...field, order: index })) };
  });
  // Imported-field bookkeeping from the removed "Import API fields" feature is dropped.
  const { dynamic_field_imports: _unused, ...metadata } = schema.metadata || {};
  return { ...schema, metadata, steps };
};


export const remapCondition = (condition: any | undefined, keyMap: Record<string, string>): any | undefined => {
  if (!condition) return condition;
  const next: any = { ...condition };
  if (next.field && keyMap[next.field]) next.field = keyMap[next.field];
  for (const group of ['all', 'any', 'none', 'one', 'not']) {
    if (Array.isArray(next[group])) next[group] = next[group].map((child: any) => remapCondition(child, keyMap));
  }
  return next;
};

export const remapFieldReferences = (field: FormField, keyMap: Record<string, string>): FormField => ({
  ...field,
  visible_when: remapCondition(field.visible_when, keyMap),
  required_when: remapCondition(field.required_when, keyMap),
  disabled_when: remapCondition(field.disabled_when, keyMap),
  reset_rules: field.reset_rules.map((rule) => ({
    ...rule,
    when: remapCondition(rule.when, keyMap)!,
    fields: rule.fields.map((key) => keyMap[key] || key),
  })),
  data_source: field.data_source ? {
    ...field.data_source,
    trigger: {
      ...field.data_source.trigger,
      depends_on: field.data_source.trigger.depends_on.map((key) => keyMap[key] || key),
      trigger_fields: (field.data_source.trigger.trigger_fields || []).map((key) => keyMap[key] || key),
    },
    request: {
      ...field.data_source.request,
      query: Object.fromEntries(Object.entries(field.data_source.request.query).map(([name, param]) => [name, {
        ...param,
        field: param.field ? (keyMap[param.field] || param.field) : param.field,
      }])),
      path: Object.fromEntries(Object.entries(field.data_source.request.path).map(([name, param]) => [name, {
        ...param,
        field: param.field ? (keyMap[param.field] || param.field) : param.field,
      }])),
      headers: Object.fromEntries(Object.entries(field.data_source.request.headers).map(([name, param]) => [name, {
        ...param,
        field: param.field ? (keyMap[param.field] || param.field) : param.field,
      }])),
      body: Object.fromEntries(Object.entries(field.data_source.request.body).map(([name, param]) => [name, {
        ...param,
        field: param.field ? (keyMap[param.field] || param.field) : param.field,
      }])),
    },
  } : undefined,
});

export const cleanConditionReferences = (condition: any | undefined, removedKeys: Set<string>): any | undefined => {
  if (!condition) return condition;
  if (condition.field && removedKeys.has(condition.field)) return undefined;
  const next: any = { ...condition };
  for (const group of ['all', 'any', 'none', 'one', 'not']) {
    if (Array.isArray(next[group])) {
      next[group] = next[group]
        .map((child: any) => cleanConditionReferences(child, removedKeys))
        .filter(Boolean);
      if (next[group].length === 0) delete next[group];
    }
  }
  if (!next.field && !next.all && !next.any && !next.none && !next.one && !next.not) return undefined;
  return next;
};

export const cleanDataSourceReferences = (dataSource: DataSource | undefined, removedKeys: Set<string>): DataSource | undefined => {
  if (!dataSource) return dataSource;
  const cleanMap = (map: Record<string, any>) => Object.fromEntries(
    Object.entries(map).filter(([, param]) => !(param?.source === 'field' && param.field && removedKeys.has(param.field))),
  );
  const trigger = {
    ...dataSource.trigger,
    depends_on: dataSource.trigger.depends_on.filter((key) => !removedKeys.has(key)),
    trigger_fields: (dataSource.trigger.trigger_fields || []).filter((key) => !removedKeys.has(key)),
  };
  if (trigger.dependency_mode === 'selected' && trigger.trigger_fields.length === 0) {
    trigger.dependency_mode = 'any';
  }
  if ((trigger.type === 'dependency_change' || trigger.type === 'step_enter_and_dependency_change') && trigger.depends_on.length === 0) {
    trigger.type = 'step_enter';
  }
  const response = { ...dataSource.response };
  if (response.dynamic_field_configs) {
    response.dynamic_field_configs = Object.fromEntries(
      Object.entries(response.dynamic_field_configs).map(([key, config]) => {
        const next = { ...config } as any;
        next.visible_when = cleanConditionReferences(next.visible_when, removedKeys);
        next.required_when = cleanConditionReferences(next.required_when, removedKeys);
        next.disabled_when = cleanConditionReferences(next.disabled_when, removedKeys);
        next.reset_rules = (next.reset_rules || [])
          .map((rule: any) => ({
            ...rule,
            when: cleanConditionReferences(rule.when, removedKeys),
            fields: (rule.fields || []).filter((target: string) => !removedKeys.has(target)),
          }))
          .filter((rule: any) => rule.when && rule.fields.length > 0);
        return [key, next];
      }),
    );
  }
  return {
    ...dataSource,
    trigger,
    request: {
      ...dataSource.request,
      query: cleanMap(dataSource.request.query),
      path: cleanMap(dataSource.request.path),
      headers: cleanMap(dataSource.request.headers),
      body: cleanMap(dataSource.request.body),
    },
    response,
  };
};

export const removeFieldReferences = (schema: FormSchema, removedKeys: Set<string>): FormSchema => {
  const cleanField = (field: FormField): FormField => ({
    ...field,
    visible_when: cleanConditionReferences(field.visible_when, removedKeys),
    required_when: cleanConditionReferences(field.required_when, removedKeys),
    disabled_when: cleanConditionReferences(field.disabled_when, removedKeys),
    reset_rules: field.reset_rules
      .map((rule) => ({
        ...rule,
        when: cleanConditionReferences(rule.when, removedKeys)!,
        fields: rule.fields.filter((target) => !removedKeys.has(target)),
      }))
      .filter((rule) => rule.when && rule.fields.length > 0),
    data_source: cleanDataSourceReferences(field.data_source, removedKeys),
  });

  const steps = schema.steps.map((item) => ({
    ...item,
    fields: item.fields.map(cleanField),
    dynamic_schema: cleanDataSourceReferences(item.dynamic_schema, removedKeys),
  }));
  const { dynamic_field_imports: _imports, ...metadata } = schema.metadata || {};

  const submission = schema.submission ? {
    ...schema.submission,
    mappings: schema.submission.mappings
      .filter((mapping) => !(mapping.source.source === 'field' && mapping.source.field && removedKeys.has(mapping.source.field)))
      .map((mapping) => ({ ...mapping, when: cleanConditionReferences(mapping.when, removedKeys) })),
    runtime_mappings: schema.submission.runtime_mappings.filter((mapping) =>
      !(mapping.scope === 'dynamic_group' && mapping.dynamic_group_key && removedKeys.has(mapping.dynamic_group_key)),
    ),
    error_mappings: schema.submission.error_mappings.map((mapping) =>
      mapping.field && removedKeys.has(mapping.field) ? { ...mapping, field: undefined } : mapping,
    ),
  } : schema.submission;

  return {
    ...schema,
    steps,
    metadata,
    submission,
  };
};

export const normalizeDynamicSchemaEndpoint = (endpoint: string | undefined): string | undefined => {
  if (!endpoint) return endpoint;
  // Older saved forms created this calibration runtime schema without the
  // application's /api/v1 prefix. The generic data-source resolver calls the
  // configured endpoint directly, so normalize that legacy value when the
  // form is loaded. New forms use the fully qualified application path.
  if (endpoint === API.calibrationForms.calibrationDataSchema) {
    return DEFAULT_DYNAMIC_FIELDS_SCHEMA_ENDPOINT;
  }
  return endpoint;
};

export function normalizeSchema(schema: FormSchema): FormSchema {
  return {
    ...schema,
    steps: schema.steps.map((s) => ({
      ...s,
      dynamic_schema: s.dynamic_schema
        ? {
            ...s.dynamic_schema,
            request: {
              ...s.dynamic_schema.request,
              endpoint: normalizeDynamicSchemaEndpoint(s.dynamic_schema.request.endpoint) || s.dynamic_schema.request.endpoint,
            },
          }
        : s.dynamic_schema,
      fields: [...s.fields]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((f, i) => ({ ...f, order: i })),
    })),
  };
}
